import { tzOffset } from '@date-fns/tz';

const MINUTE_MS = 60 * 1000;

function offsetMinutes(timeZone: string, time: number): number {
    return Number.isNaN(time) ? Number.NaN : tzOffset(timeZone, new Date(time));
}

/**
 * The engine's own Date. A test clock (Playwright's `page.clock`) can replace the global
 * Date with a function that returns plain native Dates, and a class extending that
 * function then gets native Dates too, without its own methods. A Date the replacement
 * returns still has the native constructor, so classes extend that.
 */
function nativeDateConstructor(): DateConstructor {
    const constructor: unknown = new Date(0).constructor;
    return isDateConstructor(constructor) ? constructor : Date;
}

function isDateConstructor(value: unknown): value is DateConstructor {
    return typeof value === 'function' && value.prototype === Date.prototype;
}

const NativeDate = nativeDateConstructor();

/**
 * An instant that reads as the wall clock of `timeZone`: its getters, and so date-fns
 * formatting, give the zone's year, month, day and time, and `getTimezoneOffset` the
 * zone's offset. It is for reading and formatting; its setters still work in the
 * browser's zone.
 *
 * @date-fns/tz's TZDate does the same, but extends the global Date, so under a test
 * clock that replaced it every TZDate reads the browser's zone.
 */
export class ZonedDate extends NativeDate {
    readonly timeZone: string;
    // Its UTC fields are the zone's wall clock.
    private readonly wall: Date;

    constructor(time: number, timeZone: string) {
        const offset = offsetMinutes(timeZone, time);
        super(Number.isNaN(offset) ? Number.NaN : time);
        this.timeZone = timeZone;
        this.wall = new Date(time + offset * MINUTE_MS);
    }

    getFullYear(): number {
        return this.wall.getUTCFullYear();
    }

    getMonth(): number {
        return this.wall.getUTCMonth();
    }

    getDate(): number {
        return this.wall.getUTCDate();
    }

    getDay(): number {
        return this.wall.getUTCDay();
    }

    getHours(): number {
        return this.wall.getUTCHours();
    }

    getMinutes(): number {
        return this.wall.getUTCMinutes();
    }

    getSeconds(): number {
        return this.wall.getUTCSeconds();
    }

    getMilliseconds(): number {
        return this.wall.getUTCMilliseconds();
    }

    getTimezoneOffset(): number {
        return -offsetMinutes(this.timeZone, this.getTime());
    }

    // date-fns builds the dates it derives through this, so they stay in the zone.
    [Symbol.for('constructDateFrom')](value: Date | number | string): ZonedDate {
        return new ZonedDate(new Date(value).getTime(), this.timeZone);
    }
}
